import { Box, Button, Flex, Text, useToast } from '@chakra-ui/react';
import Editor from '@monaco-editor/react';
import React, { useCallback } from 'react';
import { format } from 'sql-formatter';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { colors } from 'packages/common-frontend/colors';
import { Icon } from 'packages/common-frontend/components/icons';
import { errorToastConfig } from 'packages/common-frontend/constants';
import {
  defaultTheme,
  lightTheme,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/MonacoEditor/ui/common';
import { WipConfig } from 'packages/creditAgreements/ui/types';

const sqlFormat: (queryString: string) => string = (queryString: string) =>
  format(queryString, {
    language: 'trino', // athena uses trino & presto
  });

export const SaveQueryStringButton = ({
  newQueryString,
  changeQueryString,
  isQueryStringSaveLoading,
  selectedInputDate,
  onEditModeClose,
}: {
  newQueryString: string;
  changeQueryString: (params: {
    newQueryString: string;
    queryInputDateForColumnUpdate: Util.FullDateStr;
  }) => Promise<void>;
  isQueryStringSaveLoading: boolean;
  selectedInputDate?: Util.FullDateStr;
  onEditModeClose: () => void;
}) => {
  const toast = useToast();

  const onSaveQueryString = async () => {
    const queryInputDateForColumnUpdate =
      selectedInputDate ?? commonDate.currentDateStr();

    await changeQueryString({
      newQueryString,
      queryInputDateForColumnUpdate,
    });

    onEditModeClose();
  };
  return (
    <Button
      colorScheme="aurora"
      color="white"
      onClick={async () => {
        try {
          await onSaveQueryString();
        } catch (err) {
          toast({
            ...errorToastConfig,
            title:
              'An unexpected error occurred. Please contact Finley for support.',
            description: commonUtils.EnvUtils.isProduction()
              ? undefined
              : JSON.stringify(err),
          });
        }
      }}
      isLoading={isQueryStringSaveLoading}
      p="8px 16px"
    >
      Save & Preview
    </Button>
  );
};

type QueryStringContainerProps = {
  wipConfig?: WipConfig;
  fullPropertyKey: string;
  queryStringState: string;
  setQueryStringState: React.Dispatch<React.SetStateAction<string>>;
  editModeState: {
    isEditModeOpen: boolean;
    onEditModeOpen: () => void;
    onEditModeClose: () => void;
  };
};

export const QueryStringContainer = ({
  wipConfig,
  fullPropertyKey,
  queryStringState,
  setQueryStringState,
  editModeState,
}: QueryStringContainerProps) => {
  const { isEditModeOpen } = editModeState;

  const isClientWipMode = wipConfig?.operationMode === 'client';
  const editorTheme = isClientWipMode ? lightTheme : defaultTheme;

  const isReadOnly =
    !isEditModeOpen || !wipConfig?.isMutateAllowed(fullPropertyKey);

  const getEditorOptions = useCallback(() => {
    return isReadOnly
      ? ({
          readOnly: true,
          lineNumbers: 'off',
          overviewRulerBorder: false,
          overviewRulerLanes: 0,
          minimap: { enabled: false },
          renderLineHighlight: 'none',
          scrollbar: { vertical: 'hidden' },
        } as const)
      : ({
          readOnly: false,
          lineNumbers: 'on',
          overviewRulerBorder: true,
          overviewRulerLanes: 3,
          minimap: { enabled: false },
          renderLineHighlight: 'all',
          scrollbar: { vertical: 'auto' },
          fixedOverflowWidgets: true,
          suggest: {
            preview: true,
            showInlineDetails: true,
          },
        } as const);
  }, [isReadOnly]);

  return (
    <Box id="query-string-full-container">
      {wipConfig?.isMutateAllowed(fullPropertyKey) && !isClientWipMode && (
        <Text fontStyle="italic" fontSize="sm" mb="24px">
          Need to set up above attributes before attempting to save SQL query.
        </Text>
      )}

      <Box
        resize="vertical"
        overflowY="visible"
        h="400px"
        minH="400px"
        width="100%"
        borderWidth="1px"
        borderColor={colors.gray[200]}
        borderRadius="8px"
        p="24px 24px 24px 0"
        backgroundColor={isClientWipMode ? 'white' : undefined}
      >
        <Box
          h={isReadOnly ? '352px' : '320px'}
          minH={isReadOnly ? '352px' : '320px'}
          width="100%"
        >
          <Editor
            language="sql"
            value={queryStringState}
            onChange={(val) => setQueryStringState(val ?? '')}
            theme={editorTheme}
            options={getEditorOptions()}
          />
        </Box>
        {!isReadOnly && (
          <Flex justifyContent="flex-end" py="16px">
            <Button
              onClick={() => setQueryStringState(sqlFormat(queryStringState))}
              variant="ghost"
              p="0"
              h="20px"
            >
              <Icon icon="file-lines" boxSize={3} />
              <Text
                color={colors.black[70]}
                fontSize="12px"
                fontWeight={500}
                ml="8px"
              >
                Format SQL
              </Text>
            </Button>
          </Flex>
        )}
      </Box>
    </Box>
  );
};
